import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { supabasePublic } from '@/integrations/supabase/publicClient';
import { CombinedTable } from '@/types/table';

const naturalSort = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

export function combinedTablesQueryKey(establishmentId: string | null) {
  return ['combined-tables', establishmentId] as const;
}

export function useCombinedTablesQuery(
  establishmentId: string | null,
  useAuthenticatedData = false
) {
  return useQuery({
    queryKey: combinedTablesQueryKey(establishmentId),
    queryFn: async (): Promise<CombinedTable[]> => {
      const client = useAuthenticatedData ? supabase : supabasePublic;
      let query = client
        .from('combined_tables')
        .select('*, combined_table_tables(table_id)')
        .order('name');
      if (establishmentId) query = query.eq('establishment_id', establishmentId);

      const { data, error } = await query;
      if (error) throw error;

      const sorted = data ? [...data].sort((a, b) => naturalSort(a.name, b.name)) : [];
      const mapped: CombinedTable[] = sorted.map((ct: any) => ({
        id: ct.id,
        name: ct.name,
        tableIds: (ct.combined_table_tables as any[]).map((ctt: any) => ctt.table_id),
        totalCapacity: ct.total_capacity,
        minCapacity: ct.min_capacity ?? 1,
        isActive: ct.is_active,
        isBookable: ct.is_bookable ?? true,
        allowsChildren: ct.allows_children ?? true,
        strollerPolicy: (ct.stroller_policy ?? (ct.allows_stroller === false ? 'deny' : 'inherit')) as CombinedTable['strollerPolicy'],
        allowsStroller: (ct.stroller_policy ?? (ct.allows_stroller === false ? 'deny' : 'inherit')) !== 'deny',
        isTerrace: ct.is_terrace ?? false,
        assignmentPriority: ct.assignment_priority ?? 0,
        zoneId: ct.zone_id ?? null,
        displayOrder: ct.display_order ?? null,
        createdAt: new Date(ct.created_at),
      }));

      // Visiteur public : les combinaisons désactivées ne sont pas lisibles,
      // mais leurs réservations existantes occupent toujours des tables
      // physiques. On ajoute leur composition comme entrées inactives et non
      // réservables : jamais proposées, uniquement utilisées pour bloquer.
      if (!useAuthenticatedData && establishmentId) {
        const { data: links, error: linksError } = await supabasePublic
          .from('combined_table_tables')
          .select('combined_table_id, table_id')
          .eq('establishment_id', establishmentId);
        if (linksError) throw linksError;
        const known = new Set(mapped.map(ct => ct.id));
        const hidden = new Map<string, string[]>();
        (links ?? []).forEach((l: any) => {
          if (known.has(l.combined_table_id)) return;
          hidden.set(l.combined_table_id, [...(hidden.get(l.combined_table_id) ?? []), l.table_id]);
        });
        hidden.forEach((tableIds, id) => mapped.push({
          id, name: '', tableIds, totalCapacity: 0, minCapacity: 1,
          isActive: false, isBookable: false, allowsChildren: false, allowsStroller: false, strollerPolicy: 'deny',
          isTerrace: false, assignmentPriority: 0, zoneId: null, displayOrder: null,
          createdAt: new Date(0),
        } as CombinedTable));
      }
      return mapped;
    },
    enabled: Boolean(establishmentId),
    staleTime: 1000 * 60 * 2,
  });
}
